import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from './contexts/AuthContext';
import { useTheme } from './contexts/ThemeContext';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Pages
import Home from './pages/Home';
import Register from './pages/Register';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

function App() {
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();
  
  // Check if current route is a profile page or home page
  const isProfilePage = location.pathname.startsWith('/u/');
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // Show sidebar only on dashboard and profile pages when authenticated
  const shouldShowSidebar = isAuthenticated && !isMobile && !isHomePage;

  return (
    <HelmetProvider>
      <div className="App">
        {shouldShowSidebar && <Sidebar isMobile={false} />}
        
        {isAuthenticated && isMobile && sidebarOpen && (
          <>
            <div className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={toggleSidebar}></div>
            <Sidebar isMobile={true} toggleSidebar={toggleSidebar} />
          </>
        )}
        
        <div className={`app-container ${shouldShowSidebar ? 'with-sidebar' : ''}`}>
          {!isProfilePage && <Header isMobile={isMobile} toggleSidebar={toggleSidebar} />}
          <main className={`main-content ${isProfilePage ? 'no-header' : ''}`}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/dashboard" />} />
              <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/u/:username" element={<Profile />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </div>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable={false}
          pauseOnHover={true}
          theme={theme}
          style={{ 
            zIndex: 9999,
            position: 'fixed',
            top: '1rem',
            right: '1rem'
          }}
          limit={3}
          containerId="toast-container"
        />
      </div>
    </HelmetProvider>
  );
}

export default App; 